import { Link } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo, SITE_URL } from "@/components/Seo";
import { FaqList, JsonLd, faqJsonLd } from "@/components/FaqList";
import { PhotoHero, Eyebrow, Steps, CallButtons } from "@/components/BodyshopUi";
import { BODYSHOP_IMG, HAIL_FAQ, WA_HAIL } from "@/lib/bodyshop";

const PERKS = [
  "Nessuna verniciatura: resta la vernice originale di fabbrica",
  "Niente stucco: la lamiera torna nella sua forma",
  "Tempi rapidi, spesso in giornata per danni leggeri",
  "Costi più bassi della riparazione tradizionale",
  "Il valore dell'auto resta invariato",
  "Ti aiutiamo con la polizza eventi atmosferici",
];
const STEPS = [
  ["Valutazione", "Controlliamo l'auto sotto le lampade e contiamo i bolli: ti diamo subito tempi e preventivo."],
  ["Pratica assicurativa", "Se hai la garanzia grandine o eventi atmosferici, seguiamo noi la perizia con la compagnia."],
  ["Levabolli", "Con leve e strumenti specifici spingiamo la lamiera dall'interno, bollo per bollo, senza toccare la vernice."],
  ["Controllo finale", "Verifichiamo ogni pannello in controluce e ti riconsegniamo l'auto liscia come prima."],
];
const LD = [
  { "@context": "https://schema.org", "@type": "Service", name: "Riparazione grandine e levabolli senza verniciatura", serviceType: "Paintless Dent Repair",
    areaServed: ["Pontedera", "Ponsacco", "Valdera", "Pisa"], provider: { "@type": "AutoBodyShop", name: "Centro Auto Pontedera", telephone: "+39 0587 484791", url: SITE_URL } },
  faqJsonLd(HAIL_FAQ),
];

export default function Grandine() {
  return (
    <div className="min-h-screen bg-background grain" data-testid="grandine-page">
      <Seo title="Riparazione grandine a Pontedera — Levabolli senza verniciatura | Centro Auto Pontedera"
        description="Auto grandinata? A Pontedera togliamo i bolli da grandine senza riverniciare con la tecnica levabolli (PDR). Preventivo gratuito e gestione della polizza eventi atmosferici."
        path="/riparazione-grandine" image={BODYSHOP_IMG.hail} />
      <JsonLd id="ld-hail" data={LD[0]} />
      <JsonLd id="ld-faq" data={LD[1]} />
      <Navbar />
      <PhotoHero id="grandine" img={BODYSHOP_IMG.hailCar} wa={WA_HAIL} eyebrow="Riparazione grandine a Pontedera"
        title={<>Via i bolli.<br /><span className="amber">Senza riverniciare.</span></>}
        text="Con la tecnica levabolli (PDR) eliminiamo i danni da grandine e le piccole ammaccature lasciando intatta la vernice originale." />

      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <Link to="/carrozzeria-pontedera" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-5" data-testid="grandine-back"><ArrowLeft className="w-3.5 h-3.5" />Tutti i servizi di carrozzeria</Link>
          <Eyebrow>Cos'è il levabolli</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-5">La lamiera torna liscia, la vernice resta quella di fabbrica</h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">La riparazione senza verniciatura riporta la lamiera nella forma originale lavorando dall'interno del pannello. È la soluzione ideale per grandinate, colpi di portiera e piccole ammaccature, quando la vernice non è rovinata.</p>
          <ul className="grid sm:grid-cols-2 gap-3 mt-7" data-testid="grandine-perks">
            {PERKS.map((p) => <li key={p} className="flex gap-2.5 text-sm"><Check className="w-5 h-5 text-[#E63946] shrink-0" />{p}</li>)}
          </ul>
        </div>
        <div className="rounded-3xl overflow-hidden border border-white/10"><img src={BODYSHOP_IMG.hail} alt="Riparazione levabolli di un'ammaccatura" loading="lazy" className="w-full max-h-[460px] object-cover" /></div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20">
        <Eyebrow>Come lavoriamo</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-8">Dalla grandinata all'auto come nuova</h2>
        <Steps steps={STEPS} id="hail" />
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16">
        <Eyebrow>Domande frequenti</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-6">Grandine: le risposte</h2>
        <FaqList items={HAIL_FAQ} />
      </section>

      <section className="max-w-7xl mx-auto px-6">
        <div className="rounded-3xl bg-[#181A22] border border-white/10 p-8 lg:p-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase">Mandaci le foto dei bolli</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-3 mb-6 max-w-2xl">Una foto del cofano e del tetto alla luce basta per una prima stima. Se ti serve, durante la riparazione hai l'auto sostitutiva.</p>
          <CallButtons wa={WA_HAIL} id="grandine-bottom" />
        </div>
      </section>
      <Footer />
    </div>
  );
}
